import { call, put, race, take, takeLatest, delay } from 'redux-saga/effects';
import axios from 'axios';
import { REQUEST, CLEAR, fetchSuccess, fetchFailure } from '../../actions/datasources/loadSample';
import config from '../../../config';
import { getLocalUser } from '../../../utils/localUser';
import { Error as ErrorAction } from '../../actions/error/error';
import { buildSampleCsv } from '../../../data/sampleData';

// Load a curated sample dataset: build the CSV client-side and import it
// through the same inspect-file -> import-file path onboarding uses, so
// headline/tweet columns register as real EVENTS (not string signals), then
// poll the execution until the import completes. The slice's response holds
// the job object the dialog renders:
// { useCaseId, status: 'uploading'|'processing'|'completed'|'failed',
//   progress, name, rows, error,
//   preview: { columns, rows } }   // newest 10 rows, on 'completed' only
function* operation(action) {
  const { useCaseId } = action.payload;
  const { filename, csvText, eventColumns, header, rows: dataRows } = buildSampleCsv(useCaseId);
  // Tail preview for the completion receipt — the newest slice of exactly
  // what gets imported, captured here so nothing has to re-parse the CSV.
  const preview = { columns: header, rows: dataRows.slice(-10) };
  const file = new File([csvText], filename, { type: 'text/csv' });
  yield put(fetchSuccess({ useCaseId, status: 'uploading', progress: 0, name: filename }));
  try {
    const form = new FormData();
    form.append('file', file);
    const insp = yield call(
      axios.post,
      `${config.API_URL}/ingestion/connect/inspect-file`,
      form,
      { headers: { ...getLocalUser(), 'Content-Type': 'multipart/form-data' } },
    );
    const staged = insp.data?.data || insp.data;
    if (!staged?.table) throw new Error('Could not stage the sample file.');
    const eventSet = new Set(eventColumns);
    const fieldMappings = (staged.table.columns || [])
      .filter((c) => c.included !== false || eventSet.has(c.source_field))
      .map((c) => ({
        source_field: c.source_field,
        target_key: c.target_key,
        data_type: c.data_type,
        display_name: c.display_name || undefined,
        is_timestamp: c.is_timestamp || c.source_field === 'date',
        // Samples are wide (one series per column). Never treat a
        // measurement as a panel entity — auto-mapper name tokens like
        // "sku" / "sensor" otherwise fan the file out past plan limits.
        is_entity: false,
        is_event: eventSet.has(c.source_field),
        original_index: c.original_index,
      }));
    yield put(fetchSuccess({ useCaseId, status: 'processing', progress: 15, name: filename }));
    const res = yield call(
      axios.post,
      `${config.API_URL}/ingestion/connect/import-file`,
      {
        file_path: staged.file_path,
        file_format: staged.file_format,
        filename: staged.filename,
        field_mappings: fieldMappings,
        options: { delete_existing: false, skip_duplicates: true },
      },
      { headers: getLocalUser() },
    );
    const data = res.data?.data || res.data;

    let attempts = 0;
    while (attempts < 150) {
      attempts += 1;
      try {
        const exRes = yield call(
          axios.get,
          `${config.API_URL}/ingestion/executions/${data.execution_id}`,
          { headers: getLocalUser() },
        );
        const ex = (exRes.data?.data || exRes.data)?.execution || (exRes.data?.data || exRes.data);
        const status = ex.status;
        const progress = ex.progress?.percentage ?? Math.min(90, 10 + attempts * 4);
        const rows = ex.stats?.rows_inserted ?? 0;
        if (status === 'completed') {
          yield put(fetchSuccess({ useCaseId, status: 'completed', progress: 100, name: filename, rows, preview }));
          return;
        }
        if (status === 'failed' || status === 'cancelled') {
          yield put(fetchSuccess({ useCaseId, status: 'failed', name: filename, error: ex.error?.message || 'Import failed.' }));
          return;
        }
        yield put(fetchSuccess({ useCaseId, status: 'processing', progress, name: filename, rows }));
      } catch (_e) { /* transient — keep polling */ }
      yield delay(2000);
    }
  } catch (error) {
    yield put(ErrorAction(error));
    const msg = error.response?.data?.error || error.response?.data?.status || error.message || 'Sample import failed.';
    yield put(fetchSuccess({ useCaseId, status: 'failed', name: filename, error: msg }));
    yield put(fetchFailure(msg));
  }
}

// CLEAR aborts an in-flight import/poll (dialog reset or unmount).
function* run(action) {
  yield race([call(operation, action), take(CLEAR)]);
}

export function* watch() {
  yield takeLatest(REQUEST, run);
}
